// ============================================================================
// rfq-source-reading.ts
// ----------------------------------------------------------------------------
// Full AI never waits for the operator before Stage 5 (Peter, 2026-10-21: "the
// Ai assistant should prompt the user at stage 5 and 4 that the rfq source may
// have been different and check with user then. until that point quotemaker
// should take the best route it knows"). Where QuoteMaker is unsure which
// document is the request, it takes its best reading, carries on, and records
// the doubt as a reading. The QM Assistant raises an open reading once on
// arrival at Stage 5, and only there (Peter, 2026-10-22: "it should only
// prompt user about whether to use email or the RFQ document provided in
// Stage 5 via the AI Assistant").
//
// A reading is stored with the work it shaped, so a rebuilt report or Work
// Method replaces it:
//   - Stage 2, no request found: on the Risk report (`analysis.rfqSourceReading`);
//   - Stage 3, the attached request used: on the form (`formData.rfqSourceReading`),
//     written in the SAME save as the swap, so no saved project has the attached
//     request as its RFQ without this doubt (Peter's constraint, 2026-10-21);
//   - Stage 3, a reference no file answers: on the Work Method
//     (`projectWMS.rfq_source_readings`).
// Nothing here makes an AI call or writes a project. The per-stage prompt
// ledger is this device's (localStorage), like the cockpit tab it opens.
// ============================================================================
import { fingerprintSourceContent } from '../utils/source-provenance';
import { rebindVerifiedRfqAuthority } from '../utils/rfq-authority-binding';
import { isEmailDocument, readStoredEmail } from './stored-document-text';
import { stage1CoveringNoteAnswer, stage1CoveringNoteDecision } from './stage1-import-rfq-text';

type LooseRecord = Record<string, unknown>;

export type RfqSourceReadingKind =
    | 'attached-request-used'
    | 'request-reference-unresolved'
    | 'no-request-found';

export interface RfqSourceReadingDocument {
    documentId: string | null;
    documentName: string;
}

export interface RfqSourceSetAside extends RfqSourceReadingDocument {
    /** The full text that was not used as the request. */
    text: string;
}

export interface RfqSourceReference {
    code: string;
    /** The request's own sentence that names a document no file answered. */
    statement: string;
    statementFingerprint: string;
}

export interface RfqSourceReadingV1 {
    version: 1;
    id: string;
    kind: RfqSourceReadingKind;
    stage: 2 | 3;
    decidedAt: string;
    /** 'operator': the operator chose this source, so there is nothing to ask. */
    decidedBy: 'quotemaker' | 'operator';
    /** What QuoteMaker read as the request, or null when nothing was. */
    used: RfqSourceReadingDocument | null;
    setAside: RfqSourceSetAside[];
    /** attached-request-used: the RFQ text the swap replaced, restored by "Use the email instead". */
    replacedRequestText?: string;
    /** One plain sentence: what QuoteMaker did and why. */
    why: string;
    references?: RfqSourceReference[];
    /**
     * attached-request-used: the content keys the Stage 1 import's covering-note
     * answer (`formData.rfqCoveringNoteDecision`) is stored under, so "Use the
     * email instead" writes the same answer and the same files never ask again.
     */
    covering?: { emailFingerprint: string; requestFingerprint: string; sharedReferences: string[] };
    /** no-request-found: what the run read instead. */
    evidence?: { photos: number; documents: number; notes: boolean };
}

const text = (value: unknown): string => (typeof value === 'string' ? value : '');
const idOf = (document: unknown): string | null => {
    const id = (document as LooseRecord | null)?.id;
    return id === undefined || id === null || id === '' ? null : String(id);
};
const nameOf = (document: unknown, fallback: string): string => (
    text((document as LooseRecord | null)?.name).trim()
    || text((document as LooseRecord | null)?.originalName).trim()
    || fallback
);
const plural = (count: number, one: string, many = `${one}s`) => `${count} ${count === 1 ? one : many}`;

function withId(reading: Omit<RfqSourceReadingV1, 'id'>): RfqSourceReadingV1 {
    const identity = JSON.stringify({
        kind: reading.kind,
        stage: reading.stage,
        used: reading.used,
        setAside: reading.setAside.map(entry => [entry.documentId, fingerprintSourceContent(entry.text)]),
        references: (reading.references || []).map(entry => [entry.code, entry.statementFingerprint]),
        evidence: reading.evidence || null,
    });
    return { ...reading, id: `rfq-reading-v1:${fingerprintSourceContent(identity)}` };
}

/**
 * An email's full text for the operator to read: the stored content without its
 * leading header block, so what follows the sign-off and any quoted or forwarded
 * message stay. The same rule as the Stage 1 covering-note question
 * (`emailTextForReview`, claude/fervent-leakey-eec4b8); the clean body is the
 * fallback.
 */
export function fullEmailText(document: unknown): string {
    const lines = text((document as LooseRecord | null)?.content).replace(/\r\n?/g, '\n').split('\n');
    let index = 0;
    while (index < lines.length && (!lines[index]!.trim() || /^(?:subject|from|to|date)[ \t]*:/i.test(lines[index]!.trim()))) index += 1;
    if (lines[index]?.trim() === '---') index += 1;
    return lines.slice(index).join('\n').trim() || readStoredEmail(document).body.trim();
}

/**
 * Stage 3 used the quote request a covering email points to. What it set aside
 * is every imported email in full, the covering one first: after the swap none
 * of them is the request (Peter, 2026-10-21: "showing what was set aside").
 */
export function buildAttachedRequestReading({
    emailDocument,
    requestDocument,
    documents = [],
    replacedRequestText,
    sharedReferences = [],
    decidedAt,
}: {
    emailDocument: LooseRecord;
    requestDocument: LooseRecord;
    /** The project's documents: every email among them is listed. */
    documents?: unknown[];
    /** The RFQ text before the swap. */
    replacedRequestText: string;
    sharedReferences?: string[];
    decidedAt: string;
}): RfqSourceReadingV1 {
    const emailName = nameOf(emailDocument, 'The email');
    const requestName = nameOf(requestDocument, 'The attached request');
    const numbers = sharedReferences.length > 0 ? ` Both carry request number ${sharedReferences.join(', ')}.` : '';
    const otherEmails = (Array.isArray(documents) ? documents : []).filter((row): row is LooseRecord => (
        !!row && typeof row === 'object' && row !== emailDocument
        && idOf(row) !== idOf(emailDocument) && isEmailDocument(row)
    ));
    const setAside = [emailDocument, ...otherEmails].map((row, index) => ({
        documentId: idOf(row),
        documentName: nameOf(row, 'Email'),
        text: fullEmailText(row) || (index === 0 ? replacedRequestText : ''),
    })).filter(entry => entry.text.trim());
    return withId({
        version: 1,
        kind: 'attached-request-used',
        stage: 3,
        decidedAt,
        decidedBy: 'quotemaker',
        used: { documentId: idOf(requestDocument), documentName: requestName },
        setAside,
        replacedRequestText,
        why: `${emailName} reads as a covering note for ${requestName}, so QuoteMaker used ${requestName} as the request.${numbers}`,
        covering: {
            emailFingerprint: fingerprintSourceContent(text(emailDocument.content)),
            requestFingerprint: fingerprintSourceContent(text(requestDocument.content)),
            sharedReferences: [...sharedReferences],
        },
    });
}

/** Stage 3 went on without a document the request refers to and no file answers. */
export function buildUnresolvedReferenceReading({
    references,
    requestSource = null,
    decidedAt,
    decidedBy = 'quotemaker',
}: {
    references: RfqSourceReference[];
    requestSource?: RfqSourceReadingDocument | null;
    decidedAt: string;
    decidedBy?: RfqSourceReadingV1['decidedBy'];
}): RfqSourceReadingV1 {
    const count = references.length;
    return withId({
        version: 1,
        kind: 'request-reference-unresolved',
        stage: 3,
        decidedAt,
        decidedBy,
        used: requestSource,
        setAside: [],
        why: count > 1
            ? `The request refers to ${count} documents QuoteMaker could not match to one file each, so the Work Method follows the request text alone.`
            : 'The request refers to a document QuoteMaker could not match to one file, so the Work Method follows the request text alone.',
        references: references.map(entry => ({ ...entry })),
    });
}

/** Stage 2 ran with no file that reads as a request for quote. */
export function buildNoRequestReading({
    photos = 0,
    documents = 0,
    notes = false,
    decidedAt,
}: {
    photos?: number;
    documents?: number;
    notes?: boolean;
    decidedAt: string;
}): RfqSourceReadingV1 {
    const read: string[] = [];
    if (documents > 0) read.push(plural(documents, 'file'));
    if (photos > 0) read.push(plural(photos, 'photo'));
    if (notes) read.push('your notes');
    const from = read.length === 0
        ? 'what was entered'
        : read.length === 1 ? `the ${read[0]}` : `the ${read.slice(0, -1).join(', ')} and ${read[read.length - 1]}`;
    const why = documents === 0 && !notes
        ? `No request for quote came with this job, so QuoteMaker worked from ${from}.`
        : `No file reads as a request for quote, so QuoteMaker worked from ${from}.`;
    return withId({
        version: 1,
        kind: 'no-request-found',
        stage: 2,
        decidedAt,
        decidedBy: 'quotemaker',
        used: null,
        setAside: [],
        why,
        evidence: { photos, documents, notes },
    });
}

export function isRfqSourceReading(value: unknown): value is RfqSourceReadingV1 {
    if (!value || typeof value !== 'object') return false;
    const candidate = value as LooseRecord;
    return candidate.version === 1
        && typeof candidate.id === 'string'
        && (candidate.kind === 'attached-request-used'
            || candidate.kind === 'request-reference-unresolved'
            || candidate.kind === 'no-request-found')
        && (candidate.decidedBy === 'quotemaker' || candidate.decidedBy === 'operator')
        && typeof candidate.why === 'string'
        && Array.isArray(candidate.setAside);
}

function readingsOnWms(projectWMS: unknown): RfqSourceReadingV1[] {
    const record = projectWMS && typeof projectWMS === 'object' ? projectWMS as LooseRecord : null;
    const stored = record?.rfq_source_readings ?? record?.rfqSourceReadings;
    return Array.isArray(stored) ? stored.filter(isRfqSourceReading) : [];
}

function readingOnAnalysis(analysis: unknown): RfqSourceReadingV1[] {
    const record = analysis && typeof analysis === 'object' ? analysis as LooseRecord : null;
    const stored = record?.rfqSourceReading ?? record?.rfq_source_reading;
    return isRfqSourceReading(stored) ? [stored] : [];
}

/**
 * The operator's stored covering-note answer for exactly these two texts, read
 * as the import reads it (stage1CoveringNoteRecord: only while the RFQ is the
 * text it wrote). 'unanswered' is no answer, so the doubt is still raised.
 */
function operatorAnsweredCovering(reading: RfqSourceReadingV1, formData: LooseRecord | null | undefined): boolean {
    return !!reading.covering && stage1CoveringNoteAnswer(formData, reading.covering) !== null;
}

/** The swap's reading on the form, while the request text is still the request it adopted. */
function readingOnForm(formData: LooseRecord | null | undefined): RfqSourceReadingV1[] {
    const stored = formData?.rfqSourceReading;
    if (!isRfqSourceReading(stored) || stored.kind !== 'attached-request-used' || !stored.covering) return [];
    return fingerprintSourceContent(formData?.rfqContent) === stored.covering.requestFingerprint ? [stored] : [];
}

/**
 * The ids of the readings the operator kept at Stage 5 that store no covering-note
 * answer (a reference no file answers, no request found), kept on the project so
 * every device agrees. The prompt ledger is only this device's cache of it.
 */
function keptReadingIds(formData: LooseRecord | null | undefined): Set<string> {
    const stored = formData?.rfqSourceKeptReadingIds;
    return new Set(Array.isArray(stored) ? stored.filter((id): id is string => typeof id === 'string') : []);
}

/**
 * The readings the operator has not answered yet: QuoteMaker's own best readings
 * on the form (the swap), the current Work Method and the current Risk report.
 */
export function openRfqSourceReadings({
    analysis = null,
    projectWMS = null,
    formData = null,
}: {
    analysis?: unknown;
    projectWMS?: unknown;
    formData?: LooseRecord | null;
} = {}): RfqSourceReadingV1[] {
    const seen = new Set<string>();
    const kept = keptReadingIds(formData);
    return [...readingOnForm(formData), ...readingsOnWms(projectWMS), ...readingOnAnalysis(analysis)].filter((reading) => {
        if (reading.decidedBy !== 'quotemaker' || seen.has(reading.id) || kept.has(reading.id)) return false;
        seen.add(reading.id);
        return !operatorAnsweredCovering(reading, formData);
    });
}

// ----------------------------------------------------------------------------
// "Use the email instead"
// ----------------------------------------------------------------------------
export type SwitchToSetAsideResult =
    | { ok: true; formData: LooseRecord }
    | { ok: false; why: string };

/**
 * The form with the set-aside email back as the request: its text, the
 * receipts minted from it by the one shared function, no adoption record, and
 * the operator's answer stored the way the Stage 1 import stores it
 * (`rfqCoveringNoteDecision`, keep-email), so Stage 3 never uses the attached
 * request again for these two texts. Refused when the request text is no
 * longer the one this reading adopted.
 */
export function planSwitchToSetAsideRequest({
    reading,
    formData,
    documents = [],
    preprocessedFiles = [],
    decidedAt,
}: {
    reading: RfqSourceReadingV1;
    formData: LooseRecord | null | undefined;
    documents?: unknown[];
    preprocessedFiles?: unknown[];
    decidedAt: string;
}): SwitchToSetAsideResult {
    const restored = String(reading.replacedRequestText || '');
    if (reading.kind !== 'attached-request-used' || !reading.covering || !restored.trim()) {
        return { ok: false, why: 'There is no other request text to switch to.' };
    }
    if (!formData || fingerprintSourceContent(formData.rfqContent) !== reading.covering.requestFingerprint) {
        return { ok: false, why: 'The request text changed after this check, so nothing was switched.' };
    }
    const { rfqSourceAdoption: _adopted, rfqSourceReading: _reading, rfqCoveringNoteDecision: _earlier, ...rest } = formData;
    const next = rebindVerifiedRfqAuthority({ ...rest, rfqContent: restored } as LooseRecord, documents, preprocessedFiles);
    // The record v6.0.111's import writes and reads, so the import keeps this
    // email, never asks again about the same files, and Stage 1 shows the answer
    // with its way back.
    next.rfqCoveringNoteDecision = stage5Answer(reading, 'keep-email', next, documents, decidedAt);
    return { ok: true, formData: next };
}

/**
 * The form with the operator's Stage 5 Keep stored on it, so no device raises
 * the check again. For a swap to an attached request ("Keep <request>"), the
 * way the Stage 1 import stores a "use the attached request" answer
 * (`rfqCoveringNoteDecision`), so a later import keeps that request as the RFQ
 * the operator chose (merge review, 2026-10-22). For any other reading, its id
 * in `rfqSourceKeptReadingIds`. Null when there is nothing to store: a swap
 * whose RFQ is no longer the request it adopted, or a reading already kept.
 */
export function planKeepAttachedRequest({
    reading,
    formData,
    documents = [],
    decidedAt,
}: {
    reading: RfqSourceReadingV1;
    formData: LooseRecord | null | undefined;
    documents?: unknown[];
    decidedAt: string;
}): LooseRecord | null {
    if (!formData) return null;
    if (reading.kind !== 'attached-request-used') {
        const kept = keptReadingIds(formData);
        return kept.has(reading.id) ? null : { ...formData, rfqSourceKeptReadingIds: [...kept, reading.id] };
    }
    if (!reading.covering) return null;
    if (fingerprintSourceContent(formData.rfqContent) !== reading.covering.requestFingerprint) return null;
    return { ...formData, rfqCoveringNoteDecision: stage5Answer(reading, 'use-attached-request', formData, documents, decidedAt) };
}

/**
 * The operator's Stage 5 answer on the form as it is now: only the fields the
 * answer changed between `before` (the form it was planned from) and `answered`
 * (the plan), set or removed. An edit made while the answer was saving stays.
 */
export function withRfqSourceAnswer(
    current: LooseRecord | null | undefined,
    before: LooseRecord | null | undefined,
    answered: LooseRecord,
): LooseRecord {
    const next: LooseRecord = { ...(current || {}) };
    for (const key of new Set([...Object.keys(before || {}), ...Object.keys(answered)])) {
        if (before?.[key] === answered[key] && (key in answered) === (key in (before || {}))) continue;
        if (key in answered) next[key] = answered[key];
        else delete next[key];
    }
    return next;
}

// The operator's answer to a swap's reading at Stage 5, for the form it is written on.
function stage5Answer(
    reading: RfqSourceReadingV1,
    choice: 'keep-email' | 'use-attached-request',
    form: LooseRecord,
    documents: unknown[],
    decidedAt: string,
) {
    const covering = reading.covering!;
    // Every email the check showed, by its content, as the import's question
    // records them: a later import that brings another email asks again.
    const shown = new Set([covering.emailFingerprint]);
    for (const entry of reading.setAside) {
        const row = (Array.isArray(documents) ? documents : [])
            .find(candidate => entry.documentId !== null && idOf(candidate) === entry.documentId && isEmailDocument(candidate));
        if (row) shown.add(fingerprintSourceContent(text((row as LooseRecord).content)));
    }
    return stage1CoveringNoteDecision({
        choice,
        emailFingerprint: covering.emailFingerprint,
        requestFingerprint: covering.requestFingerprint,
        shownEmailFingerprints: [...shown],
        form,
        emailDocumentName: reading.setAside[0]?.documentName || '',
        requestDocumentName: reading.used?.documentName || '',
        sharedReferences: covering.sharedReferences,
        decidedAt,
        where: 'stage5',
    });
}

// ----------------------------------------------------------------------------
// What the Assistant says
// ----------------------------------------------------------------------------
export type RfqSourceSwitch =
    | { kind: 'use-set-aside'; label: string }
    | { kind: 'open-stage-1'; label: string };

export interface RfqSourceDoubtCopy {
    title: string;
    body: string;
    /** The request sentences no file answered, quoted. */
    quotes: string[];
    /** Everything that was set aside, in full, under one label, or null. */
    setAside: { label: string; entries: Array<{ documentName: string; text: string }> } | null;
    switchAction: RfqSourceSwitch | null;
    keepLabel: string;
}

export function describeRfqSourceDoubt(reading: RfqSourceReadingV1): RfqSourceDoubtCopy {
    if (reading.kind === 'attached-request-used') {
        const entries = reading.setAside
            .filter(entry => entry.text.trim())
            .map(entry => ({ documentName: entry.documentName, text: entry.text }));
        return {
            title: 'Check which file is the request',
            body: `${reading.why} If the email itself is the request, switch to it and QuoteMaker rebuilds the Work Method, scope and quote from it.`,
            quotes: [],
            setAside: entries.length > 0
                ? {
                    label: entries.length === 1 ? 'Show the email that was set aside' : `Show the ${entries.length} emails that were set aside`,
                    entries,
                }
                : null,
            switchAction: String(reading.replacedRequestText || '').trim() ? { kind: 'use-set-aside', label: 'Use the email instead' } : null,
            keepLabel: `Keep ${reading.used?.documentName || 'this request'}`,
        };
    }
    if (reading.kind === 'request-reference-unresolved') {
        return {
            title: 'Check the documents the request refers to',
            body: `${reading.why} If a file should have been part of the request, add it to the reference files and regenerate the Work Method.`,
            quotes: (reading.references || []).map(entry => entry.statement).filter(Boolean).slice(0, 3),
            setAside: null,
            switchAction: null,
            keepLabel: 'The request text is enough',
        };
    }
    return {
        title: 'No request for quote was found',
        body: `${reading.why} If the client sent a request, add it in Stage 1 and run the quote again.`,
        quotes: [],
        setAside: null,
        switchAction: { kind: 'open-stage-1', label: 'Add the request in Stage 1' },
        keepLabel: 'There is no request',
    };
}

// ----------------------------------------------------------------------------
// Once at Stage 5: the prompt ledger (this device). A ledger written before
// 2026-10-22 may also list Stage 4; nothing reads that entry.
// ----------------------------------------------------------------------------
export type RfqSourcePromptStage = 5;

export interface RfqSourcePromptEntry {
    /** The operator kept QuoteMaker's reading: never asked again. */
    kept?: true;
    /** Stages the Assistant was brought forward at. */
    raised?: RfqSourcePromptStage[];
    /** Stages where the operator closed the prompt without answering. */
    dismissed?: RfqSourcePromptStage[];
}

export type RfqSourcePromptLedger = Record<string, RfqSourcePromptEntry>;

const LEDGER_PREFIX = 'qm_rfq_source_prompts:';

interface LedgerStorage {
    getItem: (key: string) => string | null;
    setItem: (key: string, value: string) => void;
}

function defaultStorage(): LedgerStorage | null {
    try {
        return typeof localStorage !== 'undefined' ? localStorage : null;
    } catch {
        return null;
    }
}

export function readRfqSourcePromptLedger(projectId: string | null | undefined, storage: LedgerStorage | null = defaultStorage()): RfqSourcePromptLedger {
    if (!projectId || !storage) return {};
    try {
        const parsed = JSON.parse(storage.getItem(`${LEDGER_PREFIX}${projectId}`) || '{}');
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed as RfqSourcePromptLedger : {};
    } catch {
        return {};
    }
}

export function writeRfqSourcePromptLedger(
    projectId: string | null | undefined,
    ledger: RfqSourcePromptLedger,
    storage: LedgerStorage | null = defaultStorage(),
): void {
    if (!projectId || !storage) return;
    try {
        storage.setItem(`${LEDGER_PREFIX}${projectId}`, JSON.stringify(ledger));
    } catch {
        // Not remembered on this device: the prompt may show once more.
    }
}

function withStage(list: RfqSourcePromptStage[] | undefined, stage: RfqSourcePromptStage): RfqSourcePromptStage[] {
    return [...new Set([...(list || []), stage])].sort() as RfqSourcePromptStage[];
}

export function markRfqSourcePrompt(
    ledger: RfqSourcePromptLedger,
    readingId: string,
    event: 'raised' | 'dismissed' | 'kept',
    stage: RfqSourcePromptStage,
): RfqSourcePromptLedger {
    const entry = ledger[readingId] || {};
    const next: RfqSourcePromptEntry = event === 'kept'
        ? { ...entry, kept: true }
        : { ...entry, [event]: withStage(entry[event], stage) };
    return { ...ledger, [readingId]: next };
}

/** The open reading to bring the Assistant forward for on arrival at this stage, once. */
export function rfqSourceReadingToRaise(
    readings: RfqSourceReadingV1[],
    stage: RfqSourcePromptStage | null,
    ledger: RfqSourcePromptLedger,
): RfqSourceReadingV1 | null {
    if (stage !== 5) return null;
    return readings.find((reading) => {
        const entry = ledger[reading.id];
        return !entry?.kept && !entry?.raised?.includes(stage) && !entry?.dismissed?.includes(stage);
    }) || null;
}

/** The open reading the Assistant shows at this stage until it is answered or closed. */
export function rfqSourceReadingToShow(
    readings: RfqSourceReadingV1[],
    stage: RfqSourcePromptStage | null,
    ledger: RfqSourcePromptLedger,
): RfqSourceReadingV1 | null {
    if (stage !== 5) return null;
    return readings.find((reading) => {
        const entry = ledger[reading.id];
        return !entry?.kept && !entry?.dismissed?.includes(stage);
    }) || null;
}
